import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  ReportCompositionRevision,
  ReportCompositionSection,
  ReportRevisionRecord,
  ReportStudioEdit,
  ServerApiClient,
} from '../../../data/ServerApiClient'
import './ReportStudio.css'
import { FgHelp } from '../../help/FgHelp'

// The Report Studio edits the report's only model: the `fg.report_composition.v2`
// revision the Report sheet authors. Every edit is a pure helper from
// /excel-addin/report-revision.js applied to the head's composition, posted as
// the next revision with the head as its parent. Apply to Excel queues the
// `apply_report_draft` host operation, which the workbook owner approves in Excel.

type ReportSectionTypeSpec = { id: string; label: string; defaultTitle: string; hasCommentary?: boolean }

export type ReportStudioModules = {
  applyStudioEdit: (revision: ReportCompositionRevision, edit: ReportStudioEdit) => ReportCompositionRevision
  applyStudioEdits: (revision: ReportCompositionRevision, edits: ReportStudioEdit[]) => ReportCompositionRevision
  defaultReportRevision: (period: string) => ReportCompositionRevision
  REPORT_SECTION_TYPES: readonly ReportSectionTypeSpec[]
}

type Props = {
  appApi: ServerApiClient
  workbookId: string
  hostWorkbookInstanceId: string
  modulesOverride?: ReportStudioModules
}

type PendingApply = { revision: number; operationId: string; status: string }

const revisionModuleUrl = '/excel-addin/report-revision.js'
const sectionRegistryModuleUrl = '/excel-addin/report-section-registry.js'

async function loadModules(): Promise<ReportStudioModules> {
  const [revision, registry] = await Promise.all([
    import(/* @vite-ignore */ revisionModuleUrl),
    import(/* @vite-ignore */ sectionRegistryModuleUrl),
  ])
  return { ...revision, REPORT_SECTION_TYPES: registry.REPORT_SECTION_TYPES } as ReportStudioModules
}

function errorText(error: unknown): string {
  const err = error as { body?: unknown; message?: unknown } | null
  return String(err?.body || err?.message || error || '')
}

function isRevisionConflict(error: unknown): boolean {
  return Number((error as { status?: unknown } | null)?.status) === 409 && /report_revision_conflict/.test(errorText(error))
}

function currentMonth(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

export function ReportStudio({ appApi, workbookId, hostWorkbookInstanceId, modulesOverride }: Props) {
  const [modules, setModules] = useState<ReportStudioModules | null>(modulesOverride ?? null)
  const [head, setHead] = useState<ReportRevisionRecord | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [titleDrafts, setTitleDrafts] = useState<Record<string, string>>({})
  const [commentaryDrafts, setCommentaryDrafts] = useState<Record<string, string>>({})
  const [periodDraft, setPeriodDraft] = useState('')
  const [addType, setAddType] = useState('')
  const [aiMessage, setAiMessage] = useState('')
  const [aiTarget, setAiTarget] = useState('')
  const [pendingApply, setPendingApply] = useState<PendingApply | null>(null)
  const inFlight = useRef(false)

  const adoptHead = useCallback((next: ReportRevisionRecord | null) => {
    setHead(next)
    setTitleDrafts({})
    setCommentaryDrafts({})
    setPeriodDraft(next?.composition.period || '')
  }, [])

  const reloadHead = useCallback(async () => {
    adoptHead(await appApi.getReportRevisionHead(workbookId, hostWorkbookInstanceId))
  }, [adoptHead, appApi, hostWorkbookInstanceId, workbookId])

  useEffect(() => {
    let cancelled = false
    setLoaded(false)
    setError('')
    Promise.all([
      modulesOverride ? Promise.resolve(modulesOverride) : loadModules(),
      appApi.getReportRevisionHead(workbookId, hostWorkbookInstanceId),
    ])
      .then(([loadedModules, record]) => {
        if (cancelled) return
        setModules(loadedModules)
        adoptHead(record)
      })
      .catch((err) => { if (!cancelled) setError(errorText(err)) })
      .finally(() => { if (!cancelled) setLoaded(true) })
    return () => { cancelled = true }
  }, [adoptHead, appApi, hostWorkbookInstanceId, modulesOverride, workbookId])

  // Runs one server write. A 409 conflict means the sheet (or another editor)
  // moved the head: reload it so the next edit starts from what Excel holds.
  const run = useCallback(async (action: () => Promise<void>) => {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await action()
    } catch (err) {
      if (isRevisionConflict(err)) {
        try {
          await reloadHead()
          setNotice('The report sheet changed since you opened it. The latest revision is loaded; make your edit again.')
        } catch (reloadError) {
          setError(errorText(reloadError))
        }
      } else {
        setError(errorText(err))
      }
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }, [reloadHead])

  const postRevision = useCallback(async (
    composition: ReportCompositionRevision,
    authoredVia: 'studio' | 'ai',
    changeSummary: string,
    parentRevision: number | null,
  ) => {
    const record = await appApi.createReportRevision({
      workbook_id: workbookId,
      host_workbook_instance_id: hostWorkbookInstanceId,
      parent_revision: parentRevision,
      authored_via: authoredVia,
      change_summary: changeSummary,
      composition,
    })
    adoptHead(record)
    setPendingApply(null)
  }, [adoptHead, appApi, hostWorkbookInstanceId, workbookId])

  const edit = useCallback((studioEdit: ReportStudioEdit, changeSummary: string) => run(async () => {
    if (!head || !modules) return
    const composition = modules.applyStudioEdit(head.composition, studioEdit)
    await postRevision(composition, 'studio', changeSummary, head.revision)
  }), [head, modules, postRevision, run])

  const startReport = () => run(async () => {
    if (!modules) return
    await postRevision(modules.defaultReportRevision(currentMonth()), 'studio', 'Started report', null)
  })

  const undo = () => run(async () => {
    if (!head) return
    try {
      adoptHead(await appApi.undoReportRevision(workbookId, hostWorkbookInstanceId, head.revision))
      setPendingApply(null)
    } catch (err) {
      if (/report_revision_undo_unavailable/.test(errorText(err))) {
        setNotice('Nothing to undo: this is the first revision.')
        return
      }
      throw err
    }
  })

  const aiEdit = () => run(async () => {
    if (!head || !modules || !aiMessage.trim()) return
    const proposal = await appApi.proposeReportStudioAiEdit({
      workbook_id: workbookId,
      host_workbook_instance_id: hostWorkbookInstanceId,
      expected_revision: head.revision,
      message: aiMessage.trim(),
      target_section_id: aiTarget || null,
    })
    const composition = modules.applyStudioEdits(head.composition, proposal.edits)
    await postRevision(composition, 'ai', proposal.change_summary || 'AI edit', head.revision)
    setAiMessage('')
  })

  const applyToExcel = () => run(async () => {
    if (!head) return
    const { operation } = await appApi.applyReportRevision(workbookId, hostWorkbookInstanceId, head.id)
    setPendingApply({
      revision: head.revision,
      operationId: String(operation?.id || operation?.operation_id || ''),
      status: String(operation?.status || 'pending_approval'),
    })
  })

  if (!loaded) return <div className="fg-report-studio fg-report-studio-empty">Loading report…</div>
  if (!modules) return <div className="fg-report-studio fg-report-studio-empty" role="alert">{error || 'Report Studio could not load.'}</div>

  const sectionTypes = modules.REPORT_SECTION_TYPES
  const typeLabel = (id: string) => sectionTypes.find((spec) => spec.id === id)?.label || id
  const sections: ReportCompositionSection[] = head?.composition.sections || []
  const selectedAddType = addType || sectionTypes[0]?.id || ''

  const commitTitle = (section: ReportCompositionSection) => {
    const title = (titleDrafts[section.section_id] ?? section.title).trim()
    if (!title || title === section.title) return
    void edit({ op: 'set_title', section_id: section.section_id, title }, `Renamed "${section.title}" to "${title}"`)
  }

  return (
    <div className="fg-report-studio">
      <header className="fg-report-studio-toolbar">
        <div>
          <p>Report Studio</p>
          <h1>Monthly report<FgHelp id="report-studio.open" /></h1>
          {head ? (
            <small data-testid="report-studio-revision">
              Revision {head.revision} · {head.authored_via}{head.change_summary ? ` · ${head.change_summary}` : ''}
            </small>
          ) : null}
        </div>
        <button type="button" onClick={undo} disabled={busy || !head || head.parent_revision === null}>Undo</button><FgHelp id="report-studio.undo" />
        <button type="button" className="primary" onClick={applyToExcel} disabled={busy || !head}>Apply to Excel</button><FgHelp id="report-studio.apply" /><FgHelp id="report-studio.export" />
      </header>

      {error ? <p className="fg-report-studio-message error" role="alert">{error}</p> : null}
      {notice ? <p className="fg-report-studio-message" role="status">{notice}</p> : null}
      {pendingApply ? (
        <p className="fg-report-studio-message pending" role="status">
          Waiting for approval in Excel · revision {pendingApply.revision} · operation {pendingApply.operationId || 'queued'} ({pendingApply.status})
        </p>
      ) : null}

      {!head ? (
        <section className="fg-report-studio-panel">
          <p>This workbook has no report yet.</p>
          <button type="button" className="primary" onClick={startReport} disabled={busy}>Start a report</button>
        </section>
      ) : (
        <div className="fg-report-studio-body">
          <section className="fg-report-studio-panel">
            <h2>Report period</h2>
            <div className="fg-report-studio-row">
              <strong data-testid="report-studio-period">{head.composition.period}</strong>
              <input
                aria-label="Report period"
                type="month"
                value={periodDraft}
                onChange={(event) => setPeriodDraft(event.target.value)}
              />
              <button
                type="button"
                disabled={busy || !periodDraft || periodDraft === head.composition.period}
                onClick={() => void edit({ op: 'set_period', period: periodDraft }, `Set period to ${periodDraft}`)}
              >
                Set period
              </button>
            </div>
          </section>

          <section className="fg-report-studio-panel">
            <h2>Sections<FgHelp id="report-studio.edit_sections" /></h2>
            <ol className="fg-report-studio-sections">
              {sections.map((section, index) => (
                <li key={section.section_id} data-section-id={section.section_id}>
                  <div className="fg-report-studio-row">
                    <input
                      aria-label={`Title of ${section.title}`}
                      value={titleDrafts[section.section_id] ?? section.title}
                      disabled={busy}
                      onChange={(event) => setTitleDrafts((drafts) => ({ ...drafts, [section.section_id]: event.target.value }))}
                      onBlur={() => commitTitle(section)}
                      onKeyDown={(event) => { if (event.key === 'Enter') commitTitle(section) }}
                    />
                    <small>{typeLabel(section.section_type)}</small>
                    <button
                      type="button"
                      aria-label={`Move ${section.title} up`}
                      disabled={busy || index === 0}
                      onClick={() => void edit(
                        { op: 'move_section', section_id: section.section_id, after: index >= 2 ? sections[index - 2].section_id : null },
                        `Moved "${section.title}" up`,
                      )}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      aria-label={`Move ${section.title} down`}
                      disabled={busy || index === sections.length - 1}
                      onClick={() => void edit(
                        { op: 'move_section', section_id: section.section_id, after: sections[index + 1]?.section_id },
                        `Moved "${section.title}" down`,
                      )}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove ${section.title}`}
                      disabled={busy}
                      onClick={() => void edit({ op: 'remove_section', section_id: section.section_id }, `Removed "${section.title}"`)}
                    >
                      Remove
                    </button>
                  </div>
                  {section.commentary ? (
                    <div className="fg-report-studio-commentary"><FgHelp id="report-studio.commentary" />
                      <textarea
                        aria-label={`Commentary for ${section.title}`}
                        value={commentaryDrafts[section.section_id] ?? section.commentary.body}
                        disabled={busy}
                        onChange={(event) => setCommentaryDrafts((drafts) => ({ ...drafts, [section.section_id]: event.target.value }))}
                      />
                      <div className="fg-report-studio-row">
                        <small>{section.commentary.state}{section.commentary.author ? ` · ${section.commentary.author}` : ''}</small>
                        <button
                          type="button"
                          disabled={busy || (commentaryDrafts[section.section_id] ?? section.commentary.body) === section.commentary.body}
                          onClick={() => void edit(
                            { op: 'set_commentary', section_id: section.section_id, body: commentaryDrafts[section.section_id] ?? '', author: 'user' },
                            `Edited commentary on "${section.title}"`,
                          )}
                        >
                          Save commentary
                        </button>
                      </div>
                    </div>
                  ) : null}
                </li>
              ))}
            </ol>
            <div className="fg-report-studio-row">
              <select aria-label="Section type" value={selectedAddType} onChange={(event) => setAddType(event.target.value)}>
                {sectionTypes.map((spec) => <option key={spec.id} value={spec.id}>{spec.label}</option>)}
              </select>
              <button
                type="button"
                disabled={busy || !selectedAddType}
                onClick={() => void edit({ op: 'add_section', section_type: selectedAddType }, `Added ${typeLabel(selectedAddType)}`)}
              >
                Add section
              </button>
            </div>
          </section>

          <section className="fg-report-studio-panel">
            <h2>Ask AI to edit<FgHelp id="report-studio.ai_edit" /></h2>
            <textarea
              aria-label="AI edit request"
              placeholder="e.g. Tighten the executive summary and move the cash flow trend after the balance sheet"
              value={aiMessage}
              disabled={busy}
              onChange={(event) => setAiMessage(event.target.value)}
            />
            <div className="fg-report-studio-row">
              <select aria-label="AI edit target" value={aiTarget} onChange={(event) => setAiTarget(event.target.value)}>
                <option value="">Whole report</option>
                {sections.map((section) => <option key={section.section_id} value={section.section_id}>{section.title}</option>)}
              </select>
              <button type="button" className="primary" disabled={busy || !aiMessage.trim()} onClick={aiEdit}>Edit with AI</button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
